// /en/translate/$language — English-language hub per translation language
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
} from "@/data/translation-languages";
import { TRANSLATION_DOCTYPES } from "@/data/translation-doctypes";
import { enLanguageHubSections } from "@/data/en-translate-content";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import {
  Languages, FileCheck2, ShieldCheck, BadgeCheck, Globe2, Phone, MessageCircle, ArrowRight,
} from "lucide-react";
import TranslationLanguageDeepContent from "@/components/TranslationLanguageDeepContent";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/translate/$language/")({
  loader: ({ params }) => {
    if (!getTranslationLanguage(params.language)) throw notFound();
    return { slug: params.language };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    if (!l) return { meta: [{ title: ctaTitle("Language not found", "/en/translate/$language", "en")}] };
    const url = `${SITE}/en/translate/${l.slug}`;
    const title = `Certified ${l.nameEn} Translation in Bangkok — MFA + Embassy Accepted | NYC`;
    const desc = `Certified Thai ↔ ${l.nameEn} translation by Ministry of Justice sworn translators, ${l.turnaround}. Accepted by MFA, embassies of ${l.countryOfUse.slice(0, 3).join(", ")}, Thai courts and immigration. Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/$language", "en") },
        { name: "description", content: ctaDesc(desc, "/en/translate/$language", "en")},
        { name: "keywords", content: `certified ${l.nameEn} translation Bangkok, Thai ${l.nameEn} sworn translator, MFA legalization ${l.nameEn}, ${l.nameEn} embassy translation Thailand` },
        { property: "og:title", content: ctaTitle(title, "/en/translate/$language", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/translate/$language", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/translate/${l.slug}`, `/en/translate/${l.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Certified ${l.nameEn} Translation Service (Thailand)`,
              serviceType: "Certified document translation",
              areaServed: { "@type": "Country", name: "Thailand" },
              availableLanguage: [
                { "@type": "Language", name: "Thai", iso6391: "th" },
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: l.priceFromTh,
                priceCurrency: "THB",
                priceSpecification: { "@type": "UnitPriceSpecification", price: l.priceFromTh, priceCurrency: "THB", unitText: "page" },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: [
                { q: `Is NYC ${l.nameEn} translation accepted by the Thai Ministry of Foreign Affairs?`, a: `Yes. NYC has been a DBD-registered translation company since 2010 (registration 0435567000061). Every ${l.nameEn} translation we issue is signed by a Ministry of Justice sworn translator and is accepted by MFA Chaeng Watthana, every embassy operating in Thailand, all Thai courts, immigration, and the destination authorities in ${l.countryOfUse.slice(0, 3).join(", ")}.` },
                { q: `How much does ${l.nameEn} translation cost?`, a: `From THB ${l.priceFromTh.toLocaleString()} per A4 page (250 source words). Notary +THB 1,500/document. MFA legalization +THB 800-1,200/stamp. Embassy legalization (if non-Hague) +THB 1,500-5,500/document. All quotes are firm and include nationwide EMS.` },
                { q: `How fast can you turn around a ${l.nameEn} translation?`, a: `Standard turnaround is ${l.turnaround} from confirmed deposit. 24-hour rush is available for packages up to five pages at +50% surcharge.` },
                { q: `Do you handle full legalization, not just translation?`, a: `Yes. We coordinate the full chain in one engagement: certified translation → Notary Public → MFA legalization → Apostille or destination-embassy legalization. You make one phone call, send one set of originals, and receive one tracked package.` },
              ].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Translation", item: `${SITE}/en/translate` },
                { "@type": "ListItem", position: 3, name: `${l.nameEn} translation`, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnTranslateLanguagePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Language not found</h1>
      <Link to="/en/translate" className="mt-4 inline-block text-gold underline">Back to translation hub</Link>
    </main>
  ),
});

function EnTranslateLanguagePage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const l = getTranslationLanguage(slug)!;
  const sections = enLanguageHubSections(l);
  const others = TRANSLATION_LANGUAGES.filter((x) => x.slug !== slug).slice(0, 8);
  const featuredDocs = TRANSLATION_DOCTYPES.slice(0, 12);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate" className="hover:text-gold">Translation</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{l.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> Certified {l.nameEn}{l.nativeName ? ` · ${l.nativeName}` : ""}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Certified {l.nameEn} Translation in Thailand — <span className="text-gold">Embassy & MFA Accepted</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Thai ↔ {l.nameEn} certified translation by Ministry of Justice sworn translators, packaged with MFA legalization and embassy/Apostille handling. From THB {l.priceFromTh.toLocaleString()}/page, {l.turnaround}.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `THB ${l.priceFromTh.toLocaleString()}/pg` },
              { k: "Turnaround", v: l.turnaround },
              { k: "Certification", v: l.isCertified ? "MoJ + MFA + Notary" : "—" },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> Chat on LINE @nycli
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Language Institute (DBD 0435567000061, since 2010) provides certified Thai ↔ {l.nameEn} translation by Ministry of Justice sworn translators. From THB {l.priceFromTh.toLocaleString()}/page, delivered in {l.turnaround}, accepted by Thai MFA, all embassies in Bangkok, Thai courts, immigration, and authorities in {l.countryOfUse.slice(0, 4).join(", ")}. 4.9/5 across 2,147 verified reviews.
        </blockquote>

        {sections.map((s) => (
          <section key={s.heading} className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{s.heading}</h2>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="mt-4 text-[15px] leading-7 text-slate-700">{p}</p>
            ))}
            {s.bullets && (
              <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-800">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {b}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{l.nameEn} translation by document type</h2>
          <p className="mt-2 text-sm text-slate-600">Pick a document below for a dedicated page covering pricing, certification chain, destination uses, and FAQs.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {featuredDocs.map((d) => (
              <Link
                key={d.slug}
                to="/en/translate/$language/$doctype"
                params={{ language: l.slug, doctype: d.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{d.category}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {d.nameEn} → {l.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">From THB {(l.priceFromTh + d.surcharge).toLocaleString()} · {d.pagesEstimate}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                  See details <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <ContactCTA />
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `Is NYC ${l.nameEn} translation accepted by the Thai Ministry of Foreign Affairs?`, a: `Yes — every NYC ${l.nameEn} translation is accepted on first submission at MFA Chaeng Watthana, by every embassy in Bangkok, by Thai courts, by immigration, and by destination authorities in ${l.countryOfUse.slice(0, 4).join(", ")}.` },
                { q: `Can you handle Apostille or embassy legalization too?`, a: `Yes. We coordinate the full chain in one engagement: certified translation → Notary Public → MFA legalization → Apostille (for Hague destinations) or destination-embassy legalization.` },
                { q: `Do you offer same-day or 24-hour rush?`, a: `Yes — 24-hour rush is available at +50% surcharge for packages up to five pages. Same-day Bangkok pickup is included for jobs over THB 3,000.` },
                { q: `Where are you located? Can I drop off documents in person?`, a: `Our headquarters is at Lat Phrao 95, Bangkok, open seven days a week. We also pick up across Bangkok and accept couriered documents from anywhere in Thailand.` },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other languages we translate to and from</h2>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/en/translate/$language"
                params={{ language: o.slug }}
                className="group rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition hover:border-gold/50 hover:text-amber-700"
              >
                <div className="font-semibold group-hover:text-amber-700">{o.nameEn}</div>
                <div className="text-[11px] text-slate-500">From THB {o.priceFromTh.toLocaleString()}/pg</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TranslationLanguageDeepContent l={l} lang="en" />
      <TrustLayer variant="compact" />
    </main>
  );
}
